import { useEffect } from "react";
import { ArrowRight, Tv, Play, Globe } from "lucide-react";
import SiteHeader from "../components/site/SiteHeader";
import SiteFooter from "../components/site/SiteFooter";
import FloatingDonate from "../components/site/FloatingDonate";
import { useT } from "../i18n/LanguageContext";
import {
  IMG_WORKSHOP_HERO,
  IMG_WORKSHOP_BAGS,
  IMG_WORKSHOP_BANNER,
  IMG_WORKSHOP_GROUP,
  IMG_WORKSHOP_TALK,
  IMG_WORKSHOP_VILLAGE,
  IMG_COMMUNITY_AFTER,
  IMG_LABOR_DAY_1,
  IMG_LABOR_DAY_2,
  IMG_LABOR_DAY_3,
  IMG_LABOR_DAY_4,
  IMG_LABOR_DAY_5,
  TVT_LOME_HERO,
  TVT_LOME_VIDEO,
  TVT_LOME_PHOTOS,
  UN_TALKS_HERO,
  UN_TALKS_VIDEO,
  UN_TALKS_PHOTOS,
  ZTD_HERO,
  ZTD_PHOTOS,
} from "../components/site/siteAssets";

const copy = {
  en: {
    kicker: "Our Projects & Events",
    title: "From villages to the world stage.",
    sub: "Workshops, gatherings, and campaigns that turn lived experience into change.",

    unKicker: "Advocacy · United Nations",
    unTitle: "Talks on FGM at the UN.",
    unBody: "Photos and videos documenting Mother Hippo's participation at the United Nations, promoting awareness, education, and action to end female genital mutilation (FGM).",
    unTag: "United Nations Headquarters · New York",
    unVideoLabel: "Watch the address",

    ztdKicker: "February 6, 2026 · International Day of Zero Tolerance for FGM",
    ztdTitle: "Non à l'excision. — A village stands together.",
    ztdBody: "These photos were taken during the 2026 FGM Advocacy Workshop on the International Day of Zero Tolerance for Female Genital Mutilation in my village, Tindjassi Sarakawa, Togo — the place where I was subjected to FGM and where I now advocate for change.",
    ztdTag: "Tindjassi Sarakawa · Togo",

    laborKicker: "Event · May 1, 2026",
    laborTitle: "Honoring the women of Togo on Labor Day.",
    laborBody: "On Togo's Labor Day, the Mother Hippo Foundation gathered the women of the community — survivors, mothers, daughters, neighbors — for a day of recognition, music, food, and shared purpose. The day belongs to them.",
    laborTag: "Sokodé, Togo · Labor Day 2026",

    tvtKicker: "Television · TVT Lomé, Togo",
    tvtTitle: "TVT Lomé, Togo Interview.",
    tvtBody: "Interview broadcast by TVT in Lomé, Togo, featuring Akondo Adjei and other women discussing the impact of female genital mutilation (FGM) and efforts to promote awareness and change.",
    tvtTag: "TVT National Television · Lomé, Togo",
    tvtVideoLabel: "Watch the broadcast",
    tvtMore: "More photos and video coming soon.",

    workshopKicker: "Mission Against FGM · April 1–4, 2026",
    workshopTitle: "Togo Workshops 2026.",
    workshopSub: "Three villages. One movement.",
    workshopIntro: "In April 2026, the Mother Hippo Foundation carried out a series of impactful community workshops in Togo, reaching the villages of Tindjassi Sarakawa, Djarkpanga, and Boulohou. These workshops were led by a courageous team of women — many of them survivors of female genital mutilation — who are now using their voices to educate, empower, and protect the next generation.",
    workshopBody: "Through open dialogue, community engagement, and education, the workshops created safe spaces for women, families, and local leaders to confront the realities of FGM and explore pathways toward ending the practice. These efforts are part of a broader mission to eliminate FGM not only in Togo, but across Africa and around the world.",
    workshopCloser: "Your donation helps provide education, outreach, and protection for girls at risk, and supports survivors who are leading the fight for a future free from FGM.",
    workshopStat1: "3 villages",
    workshopStat1Sub: "Tindjassi Sarakawa · Djarkpanga · Boulohou",
    workshopStat2: "4 days",
    workshopStat2Sub: "of dialogue, education, and listening",
    workshopStat3: "Survivor-led",
    workshopStat3Sub: "by women who carry the wound — and the wisdom",

    pressKicker: "As seen in · 2017 — 2026",
    pressTitle: "The world is listening.",
    pressBody: "Four features. One mission. From the Las Vegas Review-Journal in 2017 to international magazines in 2026 — survivor voices, finally heard.",
    pressCta: "Read the original story",
    pressLvLabel: "Las Vegas Review-Journal · 2017",
    pressWomenLabel: "Women of Solutions Magazine · March 2026",
    pressEnLabel: "International feature · 2026",
    pressFrLabel: "Édition francophone · 2026",
  },
  fr: {
    kicker: "Nos Projets & Événements",
    title: "Des villages à la scène mondiale.",
    sub: "Des ateliers, des rassemblements et des campagnes qui transforment le vécu en changement.",

    unKicker: "Plaidoyer · Nations Unies",
    unTitle: "Interventions sur les MGF à l'ONU.",
    unBody: "Photos et vidéos documentant la participation de Mother Hippo aux Nations Unies, pour promouvoir la sensibilisation, l'éducation et l'action en faveur de l'élimination des mutilations génitales féminines (MGF).",
    unTag: "Siège des Nations Unies · New York",
    unVideoLabel: "Voir l'intervention",

    ztdKicker: "6 février 2026 · Journée internationale de tolérance zéro envers les MGF",
    ztdTitle: "Non à l'excision. — Un village uni.",
    ztdBody: "Ces photos ont été prises lors de l'Atelier de sensibilisation aux MGF 2026, à l'occasion de la Journée internationale de tolérance zéro envers les mutilations génitales féminines, dans mon village de Tindjassi Sarakawa, au Togo — le lieu même où j'ai été soumise aux MGF et où je milite aujourd'hui pour le changement.",
    ztdTag: "Tindjassi Sarakawa · Togo",

    laborKicker: "Événement · 1ᵉʳ mai 2026",
    laborTitle: "Honorer les femmes du Togo lors de la Fête du Travail.",
    laborBody: "Pour la Fête du Travail au Togo, la Mother Hippo Foundation a réuni les femmes de la communauté — survivantes, mères, filles, voisines — pour une journée de reconnaissance, de musique, de partage et d'engagement commun. Cette journée leur appartient.",
    laborTag: "Sokodé, Togo · Fête du Travail 2026",

    tvtKicker: "Télévision · TVT Lomé, Togo",
    tvtTitle: "Interview TVT Lomé, Togo.",
    tvtBody: "Émission diffusée par la TVT à Lomé, Togo, mettant en scène Akondo Adjei et d'autres femmes discutant de l'impact des mutilations génitales féminines (MGF) et des efforts pour la sensibilisation et le changement.",
    tvtTag: "Télévision nationale TVT · Lomé, Togo",
    tvtVideoLabel: "Regarder l'émission",
    tvtMore: "D'autres photos et la vidéo seront ajoutées prochainement.",

    workshopKicker: "Mission contre les MGF · 1–4 avril 2026",
    workshopTitle: "Ateliers du Togo 2026.",
    workshopSub: "Trois villages. Un mouvement.",
    workshopIntro: "En avril 2026, la Mother Hippo Foundation a mené une série d'ateliers communautaires marquants au Togo, dans les villages de Tindjassi Sarakawa, Djarkpanga et Boulohou. Ces ateliers ont été conduits par une équipe courageuse de femmes — dont beaucoup sont des survivantes des MGF — qui utilisent désormais leur voix pour éduquer, autonomiser et protéger la prochaine génération.",
    workshopBody: "À travers le dialogue ouvert, l'engagement communautaire et l'éducation, les ateliers ont créé des espaces sûrs où les femmes, les familles et les responsables locaux ont pu affronter la réalité des MGF et explorer les chemins pour mettre fin à cette pratique. Ces efforts s'inscrivent dans une mission plus large : éliminer les MGF non seulement au Togo, mais à travers l'Afrique et le monde entier.",
    workshopCloser: "Votre don nous permet d'offrir éducation, sensibilisation et protection aux jeunes filles à risque, et soutient les survivantes qui mènent ce combat pour un avenir libéré des MGF.",
    workshopStat1: "3 villages",
    workshopStat1Sub: "Tindjassi Sarakawa · Djarkpanga · Boulohou",
    workshopStat2: "4 jours",
    workshopStat2Sub: "de dialogue, d'éducation et d'écoute",
    workshopStat3: "Mené par les survivantes",
    workshopStat3Sub: "des femmes qui portent la blessure — et la sagesse",

    pressKicker: "Vu dans · 2017 — 2026",
    pressTitle: "Le monde écoute.",
    pressBody: "Quatre reportages. Une seule mission. Du Las Vegas Review-Journal en 2017 aux magazines internationaux en 2026 — la voix des survivantes, enfin entendue.",
    pressCta: "Lire l'article original",
    pressLvLabel: "Las Vegas Review-Journal · 2017",
    pressWomenLabel: "Women of Solutions Magazine · Mars 2026",
    pressEnLabel: "Reportage international · 2026",
    pressFrLabel: "Édition francophone · 2026",
  },
};

export default function OurProjects() {
  const t = useT(copy);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#F4EDE0] text-[#2A1F15]" style={{ fontFamily: "'Figtree','Work Sans',sans-serif" }} data-testid="our-projects-page">
      <SiteHeader />

      <section className="py-20 md:py-28 bg-[#2A1F15] text-[#F4EDE0]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold">{t.kicker}</div>
          <h1 className="mt-6 text-5xl md:text-7xl leading-[1.05]" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }} data-testid="our-projects-title">{t.title}</h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-[#F4EDE0]/85 leading-relaxed" style={{ fontFamily: "'Merriweather',serif" }}>{t.sub}</p>
        </div>
      </section>

      {/* TALKS ON FGM AT THE UN — Mother Hippo at the United Nations */}
      <section className="py-20 md:py-28 bg-[#F4EDE0]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold" data-testid="our-projects-un-kicker">
            <Globe size={14} /> {t.unKicker}
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl leading-[1.05]" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }} data-testid="our-projects-un-title">{t.unTitle}</h2>
          <p className="mt-6 max-w-3xl text-base md:text-lg text-[#2A1F15]/85 leading-relaxed" style={{ fontFamily: "'Merriweather',serif" }} data-testid="our-projects-un-body">{t.unBody}</p>
          <div className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#8C5A3C] font-bold">{t.unTag}</div>

          {/* Hero: founder at UN microphone */}
          <figure className="mt-12 overflow-hidden rounded-xl border-4 border-[#D4A93B] shadow-2xl" data-testid="our-projects-un-hero">
            <img src={UN_TALKS_HERO} alt="Mother Hippo founder addressing the United Nations on FGM" className="w-full h-auto object-cover" loading="lazy" />
          </figure>

          {/* Video player */}
          <div className="mt-10">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#E3282B] font-bold mb-4">
              <Play size={12} /> {t.unVideoLabel}
            </div>
            <div className="relative w-full aspect-video rounded-xl overflow-hidden border-4 border-[#2A1F15] shadow-2xl bg-black">
              <video
                src={UN_TALKS_VIDEO}
                controls
                preload="metadata"
                playsInline
                poster={UN_TALKS_HERO}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="our-projects-un-video"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>

          {/* Photo grid */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="our-projects-un-grid">
            {UN_TALKS_PHOTOS.map((url, i) => {
              const borders = ["border-[#E3282B]", "border-[#D4A93B]", "border-[#00A651]"];
              return (
                <figure
                  key={i}
                  data-testid={`our-projects-un-photo-${i}`}
                  className={`aspect-square overflow-hidden rounded-xl border-4 shadow-md ${borders[i % borders.length]}`}
                >
                  <img src={url} alt="Mother Hippo at the United Nations" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </figure>
              );
            })}
          </div>
        </div>
      </section>

      {/* ZERO TOLERANCE DAY WORKSHOP — Tindjassi Sarakawa, Feb 6, 2026 */}
      <section className="py-20 md:py-28 bg-[#1A0F0A] text-[#F4EDE0]" data-testid="our-projects-ztd">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold" data-testid="our-projects-ztd-kicker">{t.ztdKicker}</div>
          <h2 className="mt-4 text-4xl md:text-6xl leading-[1.02]" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }} data-testid="our-projects-ztd-title">{t.ztdTitle}</h2>
          <p className="mt-6 max-w-3xl text-base md:text-lg leading-[1.85] text-[#F4EDE0]/85" style={{ fontFamily: "'Merriweather',serif" }} data-testid="our-projects-ztd-body">{t.ztdBody}</p>
          <div className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#D4A93B] font-bold">{t.ztdTag}</div>

          {/* Hero — wide group with "NON A L'EXCISION" signs */}
          <figure className="mt-12 overflow-hidden rounded-xl border-4 border-[#E3282B] shadow-2xl" data-testid="our-projects-ztd-hero">
            <img src={ZTD_HERO} alt="Women of Tindjassi Sarakawa holding 'Non à l'excision' signs — International Day of Zero Tolerance for FGM 2026" className="w-full h-auto object-cover" loading="lazy" />
          </figure>

          {/* Supporting photo grid */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="our-projects-ztd-grid">
            {ZTD_PHOTOS.map((url, i) => {
              const borders = ["border-[#D4A93B]", "border-[#00A651]", "border-[#E3282B]"];
              return (
                <figure
                  key={i}
                  data-testid={`our-projects-ztd-photo-${i}`}
                  className={`aspect-square overflow-hidden rounded-xl border-4 shadow-xl ${borders[i % borders.length]}`}
                >
                  <img src={url} alt="Zero Tolerance Day Workshop — Tindjassi Sarakawa, Togo" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </figure>
              );
            })}
          </div>
        </div>
      </section>

      {/* LABOR DAY — May 1, 2026 */}
      <section className="py-20 md:py-28 bg-white border-y-4 border-[#D4A93B]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold" data-testid="our-projects-labor-kicker">{t.laborKicker}</div>
          <h2 className="mt-4 text-4xl md:text-5xl leading-[1.05]" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }} data-testid="our-projects-labor-title">{t.laborTitle}</h2>
          <p className="mt-6 max-w-3xl text-base md:text-lg text-[#2A1F15]/85 leading-relaxed" style={{ fontFamily: "'Merriweather',serif" }}>{t.laborBody}</p>
          <div className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#8C5A3C] font-bold">{t.laborTag}</div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="our-projects-labor-grid">
            <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#D4A93B] shadow-md md:row-span-2 md:aspect-auto">
              <img src={IMG_LABOR_DAY_3} alt="Women honored at Mother Hippo Labor Day gathering, Togo" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#E3282B] shadow-md">
              <img src={IMG_LABOR_DAY_1} alt="Mother Hippo community Labor Day" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#00A651] shadow-md">
              <img src={IMG_LABOR_DAY_2} alt="Mother Hippo community in Togo" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#8C5A3C] shadow-md">
              <img src={IMG_LABOR_DAY_4} alt="Community gathering" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#2A1F15] shadow-md">
              <img src={IMG_LABOR_DAY_5} alt="Voice of the foundation" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          </div>
        </div>
      </section>

      {/* TVT LOMÉ, TOGO — Television interview */}
      <section className="py-20 md:py-28 bg-[#1A0F0A] text-[#F4EDE0]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold" data-testid="our-projects-tvt-kicker">
            <Tv size={14} /> {t.tvtKicker}
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl leading-[1.05]" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }} data-testid="our-projects-tvt-title">{t.tvtTitle}</h2>
          <p className="mt-6 max-w-3xl text-base md:text-lg text-[#F4EDE0]/85 leading-relaxed" style={{ fontFamily: "'Merriweather',serif" }}>{t.tvtBody}</p>
          <div className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#D4A93B] font-bold">{t.tvtTag}</div>

          {/* Studio hero shot */}
          <figure className="mt-12 overflow-hidden rounded-xl border-4 border-[#D4A93B] shadow-2xl" data-testid="our-projects-tvt-hero">
            <img src={TVT_LOME_HERO} alt="TVT Lomé Togo studio panel — Akondo Adjei and other women discussing FGM" className="w-full h-auto object-cover" loading="lazy" />
          </figure>

          {/* Video player */}
          <div className="mt-10">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#E3282B] font-bold mb-4">
              <Play size={12} /> {t.tvtVideoLabel}
            </div>
            <div className="relative w-full aspect-video rounded-xl overflow-hidden border-4 border-[#2A1F15] shadow-2xl bg-black">
              <video
                src={TVT_LOME_VIDEO}
                controls
                preload="metadata"
                playsInline
                poster={TVT_LOME_HERO}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="our-projects-tvt-video"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>

          {/* Photo grid */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="our-projects-tvt-grid">
            {TVT_LOME_PHOTOS.map((url, i) => {
              const borders = ["border-[#E3282B]", "border-[#D4A93B]", "border-[#00A651]", "border-[#8C5A3C]", "border-[#F4EDE0]", "border-[#E3282B]"];
              const span = i === 0 ? "md:row-span-2 md:aspect-auto" : "";
              return (
                <figure
                  key={i}
                  data-testid={`our-projects-tvt-photo-${i}`}
                  className={`aspect-square overflow-hidden rounded-xl border-4 shadow-md ${borders[i % borders.length]} ${span}`}
                >
                  <img src={url} alt="TVT Lomé Togo interview" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </figure>
              );
            })}
          </div>

          <p className="mt-8 text-sm italic text-[#F4EDE0]/65 max-w-2xl">{t.tvtMore}</p>
        </div>
      </section>

      {/* TOGO WORKSHOPS — April 1-4, 2026 */}
      <section className="py-20 md:py-28 bg-[#F4EDE0]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold" data-testid="our-projects-workshop-kicker">{t.workshopKicker}</div>
            <h2 className="mt-4 text-4xl md:text-6xl leading-[1.05]" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }} data-testid="our-projects-workshop-title">{t.workshopTitle}</h2>
            <p className="mt-3 italic text-2xl md:text-3xl text-[#8C5A3C]" style={{ fontFamily: "'Merriweather',serif" }}>{t.workshopSub}</p>
          </div>

          <div className="mt-12 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-8">
              <div className="aspect-[16/10] overflow-hidden rounded-xl border-4 border-[#2A1F15] shadow-2xl">
                <img src={IMG_WORKSHOP_HERO} alt="Mother Hippo Togo workshop, April 2026" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="col-span-12 md:col-span-4 flex flex-col gap-4">
              <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#00A651]">
                <img src={IMG_WORKSHOP_BAGS} alt="Women holding FGM awareness bags" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square overflow-hidden rounded-xl border-4 border-[#D4A93B]">
                <img src={IMG_WORKSHOP_BANNER} alt="Workshop participants behind an end-FGM banner" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[{ b: t.workshopStat1, s: t.workshopStat1Sub, c: "#E3282B" }, { b: t.workshopStat2, s: t.workshopStat2Sub, c: "#00A651" }, { b: t.workshopStat3, s: t.workshopStat3Sub, c: "#D4A93B" }].map((x, i) => (
              <div key={i} data-testid={`our-projects-workshop-stat-${i}`} className="rounded-xl border-2 border-[#2A1F15]/10 bg-white p-6 md:p-8">
                <div className="text-3xl md:text-4xl" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700, color: x.c }}>{x.b}</div>
                <div className="mt-2 text-sm md:text-base text-[#2A1F15]/80 leading-relaxed">{x.s}</div>
              </div>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-12 gap-10 items-start">
            <div className="col-span-12 md:col-span-7 space-y-5 text-base md:text-lg leading-[1.85] text-[#2A1F15]/90" style={{ fontFamily: "'Merriweather',serif" }}>
              <p>{t.workshopIntro}</p>
              <p>{t.workshopBody}</p>
              <p className="text-[#8C5A3C] font-semibold italic">{t.workshopCloser}</p>
            </div>
            <div className="col-span-12 md:col-span-5 grid grid-cols-2 gap-4">
              <div className="aspect-[3/4] overflow-hidden rounded-lg"><img src={IMG_WORKSHOP_GROUP} alt="Women gather at a Togo workshop" className="w-full h-full object-cover" /></div>
              <div className="aspect-[3/4] overflow-hidden rounded-lg mt-8"><img src={IMG_WORKSHOP_TALK} alt="Survivor speaks at a workshop" className="w-full h-full object-cover" /></div>
              <div className="aspect-[3/4] overflow-hidden rounded-lg"><img src={IMG_WORKSHOP_VILLAGE} alt="Village participants in Togo" className="w-full h-full object-cover" /></div>
              <div className="aspect-[3/4] overflow-hidden rounded-lg mt-8"><img src={IMG_COMMUNITY_AFTER} alt="Community gathering after a workshop" className="w-full h-full object-cover" /></div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
      <FloatingDonate />
    </div>
  );
}
